import { queryOptions } from "@tanstack/react-query";
import type { ColumnFiltersState, PaginationState, SortingState } from "@tanstack/react-table";
import moment from "moment";
import { useDates } from "../../../dates/DatesContext";
import { apiClient } from "../../../zodios/client";
import type { DatasetsQueryFilter } from "../../../zodios/apiDefinitions/api/dsp/common";
import useDSPSellerInfo from "./useDSPSellerInfo";
import { clientToServerSorting } from "../../../components/table/utils";
import { adaptDatasetsFilters } from "../../../utils/datasetsFilters";

type Params = {
  pagination: PaginationState;
  sorting: SortingState;
  filters?: ColumnFiltersState;
  search?: string;
};

export function useGetDSPInventoryPaginated(options: { lineItemId: string | number }) {
  const { start, end, preStart, preEnd } = useDates();
  const { dspAdvertiser } = useDSPSellerInfo();

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");

  return (params: Params) => {
    const columnFilters = adaptDatasetsFilters(params.filters);
    const searchFilter: DatasetsQueryFilter[] =
      params.search ? [{ id: "site", value: { op: "contains", value: params.search } }] : [];

    const filters: DatasetsQueryFilter[] = [
      { id: "start_date", value: { op: "gte", value: startDate } },
      { id: "start_date", value: { op: "lte", value: endDate } },
      { id: "date_prev", value: { op: "gte", value: preStartDate } },
      { id: "date_prev", value: { op: "lte", value: preEndDate } },
      { id: "line_item_id", value: { op: "eq", value: options.lineItemId } },
      ...columnFilters,
      ...searchFilter,
    ];

    if (dspAdvertiser?.id) {
      filters.push({ id: "dsp_seller_info_id", value: { op: "eq", value: dspAdvertiser.id } });
    }

    return queryOptions({
      queryKey: [
        {
          entity: "dspInventoryPaginated",
          advertiserId: dspAdvertiser?.id,
          params,
          dates: { startDate, endDate, preStartDate, preEndDate },
          lineItemId: options.lineItemId,
        },
      ],
      queryFn: async () => {
        const res = await apiClient.dspInventoryDatasetsQuery({
          model: "dspinventory",
          dimensions: "site",
          entity_mode: "none",
          filters,
          sort: clientToServerSorting(params.sorting),
          page: params.pagination.pageIndex + 1,
          limit: params.pagination.pageSize,
          include_count: true,
        });
        return { data: res.data, count: res.pagination.total ?? res.data.length };
      },
      staleTime: 30000,
    });
  };
}
